<template>
  <div class="search-result-item" :class="{ 'is-focused': isFocused }" @click="$emit('select', { ...item, module: item.module || module })">
    <!-- Avatar -->
    <div class="result-avatar" :class="`result-avatar--${module}`">
      <i :class="moduleIcon"></i>
    </div>

    <!-- Body -->
    <div class="result-body">

      <!-- Employees -->
      <template v-if="module === 'employees'">
        <span class="result-primary">{{ employeeName }}</span>
        <span class="result-secondary">
          <span v-if="item.hcm_id">{{ item.hcm_id }}</span>
          <span v-if="item.department">· {{ item.department }}</span>
          <span v-if="item.designation">· {{ item.designation }}</span>
        </span>
      </template>

      <!-- Attendance -->
      <template v-else-if="module === 'attendance'">
        <span class="result-primary">{{ fullName(item) }}</span>
        <span class="result-secondary">
          <span v-if="item.day || item.date">{{ item.day || item.date }}</span>
          <span v-if="item.punchin_time">· In: {{ item.punchin_time }}</span>
          <span v-if="item.punchout_time">· Out: {{ item.punchout_time }}</span>
          <span v-if="item.status" :class="statusClass(item.status)">{{ item.status }}</span>
        </span>
      </template>

      <!-- Leaves -->
      <template v-else-if="module === 'leaves'">
        <span class="result-primary">{{ fullName(item) }}</span>
        <span class="result-secondary">
          <span v-if="item.leave_type">{{ item.leave_type }}</span>
          <span v-if="item.from && item.to">· {{ item.from }} → {{ item.to }}</span>
          <span v-if="item.status" :class="statusClass(item.status)">{{ item.status }}</span>
        </span>
      </template>

      <!-- Assets & Services -->
      <template v-else-if="module === 'assets'">
        <span class="result-primary">{{ assetEmployeeName(item) }}</span>
        <span class="result-secondary">
          <span v-if="item.hcm_id">{{ item.hcm_id }}</span>
          <template v-if="item.matched && item.matched.length">
            <span>· <span class="gs-badge gs-badge-secondary">{{ assetTypeLabel(item.matched[0].type) }}</span></span>
            <span v-if="item.matched[0].value">· {{ item.matched[0].value }}</span>
          </template>
        </span>
      </template>

      <!-- Letters & Incidents -->
      <template v-else-if="module === 'letters'">
        <span class="result-primary">{{ letterTitle(item) }}</span>
        <span class="result-secondary">
          <span v-if="item.incident_type">{{ item.incident_type }}</span>
          <span v-if="item.name || item.hcm_id">· {{ letterEmployeeName(item) }}</span>
          <span v-if="item.hcm_id">· {{ item.hcm_id }}</span>
          <span v-if="item.status" :class="statusClass(item.status)">{{ item.status }}</span>
        </span>
      </template>

    </div>
  </div>
</template>

<script>
const MODULE_ICONS = {
  employees:  "fa-solid fa-user",
  attendance: "fa-solid fa-clock",
  leaves:     "fa-solid fa-calendar-days",
  assets:     "fa-solid fa-box-open",
  letters:    "fa-solid fa-file-lines",
};

export default {
  name: "SearchResultItem",

  props: {
    item:      { type: Object, required: true },
    module:    { type: String, required: true },
    isFocused: { type: Boolean, default: false },
  },

  computed: {
    moduleIcon() {
      return MODULE_ICONS[this.module] || "fa-solid fa-circle";
    },
    employeeName() {
      if (this.item.name) return this.item.name;
      const parts = [this.item.first_name, this.item.middle_name, this.item.last_name].filter(Boolean);
      return parts.length ? parts.join(" ") : this.item.hcm_id || "—";
    },
  },

  methods: {
    fullName(item) {
      if (item.name) return item.name;
      const parts = [item.first_name, item.middle_name, item.last_name].filter(Boolean);
      return parts.length ? parts.join(" ") : item.hcm_id || "";
    },

    assetEmployeeName(item) {
      const base = item.name || this.fullName(item) || item.hcm_id || "—";
      return item.salutation ? `${item.salutation} ${base}` : base;
    },

    assetTypeLabel(type) {
      const map = { asset: "Asset", card: "Card", service: "Service", inventory: "Inventory" };
      return map[type] || type;
    },

    letterTitle(item) {
      if (item.letter && item.incident_type) return `${item.letter} — ${item.incident_type}`;
      return item.letter || item.incident_type || item.title || "Letter/Incident";
    },

    letterEmployeeName(item) {
      const base = item.name || this.fullName(item) || item.hcm_id || "—";
      return item.salutation ? `${item.salutation} ${base}` : base;
    },

    statusClass(status) {
      const map = {
        approved: "gs-badge gs-badge-success",
        present:  "gs-badge gs-badge-success",
        pending:  "gs-badge gs-badge-warning",
        late:     "gs-badge gs-badge-warning",
        rejected: "gs-badge gs-badge-danger",
        absent:   "gs-badge gs-badge-danger",
      };
      return map[(status || "").toLowerCase()] || "gs-badge gs-badge-secondary";
    },
  },
};
</script>

<style>
.result-avatar--employees  { background: #dbeafe; color: #2563eb; }
.result-avatar--attendance { background: #ffedd5; color: #ea580c; }
.result-avatar--leaves     { background: #dcfce7; color: #16a34a; }
.result-avatar--assets     { background: #ede9fe; color: #7c3aed; }
.result-avatar--letters    { background: #fce7f3; color: #db2777; }
</style>
